import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Space, Tooltip } from 'antd'
import { useIntl } from 'react-intl'
import useMemoizeCheckPermission from 'utils/hooks/useMemoizeCheckPermission'
import { compareWithJSON } from 'utils/helper'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash-es'
import arrowSvgUrl from 'assets/images/arrow.svg?url'
import { animate } from 'motion'
import { outletsSelector } from '@/redux/selectors'
import ProfileMenu from './ProfileMenu'
import LanguageSelector from './LanguageSelector'
import CompanySelector from './CompanySelector'
import Actions from './Actions'
import Timelines from './Timelines'
import ThemeColors from './ThemeColors'
import './style.scss'

const SHORTCUT_ICON_BASE = 'resources/images/icons/menu-v2'
const ICON_ONLY_MAX_WIDTH = 1280

const ShortcutBtn = ({ label, showTooltip, className, ...props }) => {
  const button = (
    <Button
      className={className ? `shortcut-btn ${className}` : 'shortcut-btn'}
      aria-label={label}
      {...props}
    >
      <span className="shortcut-label">{label}</span>
    </Button>
  )

  if (!showTooltip) {
    return button
  }

  return <Tooltip title={label}>{button}</Tooltip>
}

/** Ruang lokal assets/images/arrow.svg (200×200). Acuan bbox path ≈ y 49–152, x ~48–143; pusat ≈ (96, 100.5). */
const KASIR_GUIDE_LX0 = 96
const KASIR_GUIDE_LY0 = 152
const KASIR_GUIDE_LY1 = 49

const KASIR_ARROW_ASSET_SIZE = 200
/** Label di kanan ikon (ruang lokal sama dengan viewBox panah). */
const KASIR_GUIDE_CTA_TEXT_X = 206
const KASIR_GUIDE_CTA_TEXT_Y = KASIR_ARROW_ASSET_SIZE / 2
const KASIR_ARROW_LOCAL_HEIGHT = KASIR_GUIDE_LY0 - KASIR_GUIDE_LY1
const KASIR_ARROW_ANCHOR_Y = (KASIR_GUIDE_LY0 + KASIR_GUIDE_LY1) / 2

/** Skala disesuaikan canvas 200 & tinggi konten ~103 (bukan 512/413). */
const KASIR_ARROW_SCALE_MIN = 0.3
const KASIR_ARROW_SCALE_MAX = 0.92
const KASIR_ARROW_GAP_FRACTION = 0.72
const KASIR_ARROW_MAX_PX = 168
/** Skala horizontal < 1 memperamping siluet tanpa memendekkan panah. */
const KASIR_ARROW_SLIM_X = 0.66
/** Geser horizontal ke kiri relatif terhadap pusat tombol Kasir (px). */
const KASIR_ARROW_OFFSET_X = -32
/** Geser label CTA ke bawah setelah hitung posisi (px, sumbu Y layar). */
const KASIR_GUIDE_CTA_OFFSET_Y = 24
/** Rotasi searah jarum jam (derajat) — miring ke kanan sedikit. */
const KASIR_ARROW_TILT_DEG = 9

/** Gerak maju-munduh (px, sumbu Y layar); pakai translateY lewat animate() agar SVG <g> benar-benar bergerak. */
const KASIR_ARROW_BOB_PX = [0, -14, 0]

const kasirArrowBobTransition = {
  duration: 1.15,
  repeat: Infinity,
  ease: 'easeInOut',
}

function buildKasirArrowGapTransform(centerX, centerY, scale) {
  const sx = scale * KASIR_ARROW_SLIM_X
  const sy = scale
  return `translate(${centerX}, ${centerY}) rotate(${KASIR_ARROW_TILT_DEG}) scale(${sx}, ${sy}) translate(${-KASIR_GUIDE_LX0}, ${-KASIR_ARROW_ANCHOR_Y})`
}

/**
 * Peta titik ruang lokal (sama seperti <image> 0–200) ke koordinat SVG layar.
 * Rantai sama dengan `buildKasirArrowGapTransform`: T(cx,cy)·R(θ)·S(sx,sy)·T(-anchor).
 * Rotasi SVG `rotate(θ)` dengan θ positif = searah jarum jam (sumbu Y ke bawah).
 */
function kasirMapLocalPointToScreen(centerX, centerY, scale, localX, localY) {
  const sx = scale * KASIR_ARROW_SLIM_X
  const sy = scale
  const rad = (KASIR_ARROW_TILT_DEG * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = localX - KASIR_GUIDE_LX0
  const dy = localY - KASIR_ARROW_ANCHOR_Y
  const sxp = dx * sx
  const syp = dy * sy
  const xr = sxp * cos + syp * sin
  const yr = -sxp * sin + syp * cos
  return { x: centerX + xr, y: centerY + yr }
}

const CustomTopBar = ({
  translation,
  isMobileView,
  companies,
  isSuperUser,
  onChangeCompany,
  darkMode,
  onOpenGlobalSearch,
}) => {
  const [showKasirPointer, setShowKasirPointer] = useState(false)
  const [sourceArea, setSourceArea] = useState(null)
  const [arrowTransform, setArrowTransform] = useState('')
  const [ctaLabelPos, setCtaLabelPos] = useState(null)
  const [viewportSize, setViewportSize] = useState({ width: 1366, height: 768 })
  const [arrowNonce, setArrowNonce] = useState(0)
  const hideTimerRef = useRef(null)
  const kasirArrowBobRef = useRef(null)
  const kasirCtaLabelBobRef = useRef(null)

  const { formatMessage } = useIntl()
  const isHasAccessAudit = useMemoizeCheckPermission({ permission: 'audits' })
  const isCanAddInvoice = useMemoizeCheckPermission({ permission: 'invoices_add' })
  const isCanAddPurchaseInvoice = useMemoizeCheckPermission({ permission: 'purchase_invoices_add' })
  const isCanAddExpense = useMemoizeCheckPermission({ permission: 'expenses_add' })
  const outlets = useSelector(outletsSelector)

  const selectedCompany = useMemo(
    () => companies.companies.filter((e) => e.endpoint === companies.selectedEndpoint),
    [companies],
  )

  useEffect(() => {
    const updateViewport = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight })
    }
    updateViewport()
    window.addEventListener('resize', updateViewport)

    const handler = (event) => {
      const nextSourceArea = event?.detail?.sourceRect
      if (
        nextSourceArea?.left != null &&
        nextSourceArea?.top != null &&
        nextSourceArea?.right != null &&
        nextSourceArea?.bottom != null
      ) {
        setSourceArea(nextSourceArea)
      }
      setArrowNonce((n) => n + 1)
      setShowKasirPointer(true)
    }

    window.addEventListener('kledo:show-kasir-pointer', handler)
    return () => {
      window.removeEventListener('kledo:show-kasir-pointer', handler)
      window.removeEventListener('resize', updateViewport)
    }
  }, [])

  useEffect(() => {
    if (!showKasirPointer || !arrowTransform || !ctaLabelPos) {
      return undefined
    }
    const elArrow = kasirArrowBobRef.current
    const elLabel = kasirCtaLabelBobRef.current
    if (!elArrow) {
      return undefined
    }
    const [a, b, c] = KASIR_ARROW_BOB_PX
    const keyframes = {
      transform: [`translateY(${a}px)`, `translateY(${b}px)`, `translateY(${c}px)`],
    }
    const controlsArrow = animate(elArrow, keyframes, kasirArrowBobTransition)
    const controlsLabel = elLabel ? animate(elLabel, keyframes, kasirArrowBobTransition) : null
    return () => {
      controlsArrow.stop()
      controlsLabel?.stop()
    }
  }, [showKasirPointer, arrowTransform, arrowNonce, ctaLabelPos])

  useLayoutEffect(() => {
    if (!showKasirPointer) {
      setArrowTransform('')
      setCtaLabelPos(null)
      return undefined
    }

    const vw = window.innerWidth
    const vh = window.innerHeight
    setViewportSize({ width: vw, height: vh })

    const kasirButton = document.getElementById('kledo-kasir-button')
    if (!kasirButton) {
      setShowKasirPointer(false)
      return undefined
    }

    const rect = kasirButton.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2 + KASIR_ARROW_OFFSET_X

    const kasirBottom = rect.bottom
    const bannerTop =
      sourceArea?.top != null ? Math.max(sourceArea.top, kasirBottom + 28) : kasirBottom + 88
    const gapHeight = Math.max(28, bannerTop - kasirBottom)

    const centerY = kasirBottom + gapHeight / 2

    const targetPx = Math.min(
      KASIR_ARROW_MAX_PX,
      gapHeight * KASIR_ARROW_GAP_FRACTION,
      (gapHeight - 8) * 0.95,
    )
    const rawScale = targetPx / KASIR_ARROW_LOCAL_HEIGHT
    const scale = Math.min(KASIR_ARROW_SCALE_MAX, Math.max(KASIR_ARROW_SCALE_MIN, rawScale))

    setArrowTransform(buildKasirArrowGapTransform(centerX, centerY, scale))
    const labelPt = kasirMapLocalPointToScreen(
      centerX,
      centerY,
      scale,
      KASIR_GUIDE_CTA_TEXT_X,
      KASIR_GUIDE_CTA_TEXT_Y,
    )
    setCtaLabelPos({
      x: labelPt.x,
      y: labelPt.y + KASIR_GUIDE_CTA_OFFSET_Y,
    })

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
    }
    hideTimerRef.current = setTimeout(() => {
      setShowKasirPointer(false)
    }, 5500)

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
      }
      hideTimerRef.current = null
    }
  }, [showKasirPointer, arrowNonce, sourceArea])

  const handleKasirClick = useCallback(() => {
    setShowKasirPointer(false)
  }, [])

  const sellLabel = formatMessage({ id: 'button.sell' })
  const buyLabel = formatMessage({ id: 'button.buy' })
  const expenseLabel = translation.menu
    ? translation.menu.expenses
    : formatMessage({ id: 'financeExpenses.financeExpenses' })
  const cashierLabel = formatMessage({ id: 'cashier' })
  const searchLabel = formatMessage({ id: 'search' })
  const isIconOnlyShortcut = viewportSize.width <= ICON_ONLY_MAX_WIDTH

  return (
    <div className={`topbar ${darkMode ? 'bg-dark text-light' : 'bg-light'}`}>
      {!isMobileView && (
        <Space size={8} className="shortcut-group">
          {isCanAddInvoice && (
            <ShortcutBtn
              label={sellLabel}
              showTooltip={isIconOnlyShortcut}
              href="/#/sales/invoices/add"
              icon={
                <img
                  className="shortcut-icon"
                  src={`${SHORTCUT_ICON_BASE}/action-jual.svg`}
                  alt=""
                />
              }
            />
          )}
          {isCanAddPurchaseInvoice && (
            <ShortcutBtn
              label={buyLabel}
              showTooltip={isIconOnlyShortcut}
              href="/#/purchases/purchaseinvoices/add"
              icon={
                <img
                  className="shortcut-icon"
                  src={`${SHORTCUT_ICON_BASE}/action-beli.svg`}
                  alt=""
                />
              }
            />
          )}
          {isCanAddExpense && (
            <ShortcutBtn
              label={expenseLabel}
              showTooltip={isIconOnlyShortcut}
              href="/#/expenses/add"
              icon={
                <img
                  className="shortcut-icon"
                  src={`${SHORTCUT_ICON_BASE}/action-biaya.svg`}
                  alt=""
                />
              }
            />
          )}
          {!isEmpty(outlets) && (
            <ShortcutBtn
              label={cashierLabel}
              showTooltip={isIconOnlyShortcut}
              href="/#/cashier"
              id="kledo-kasir-button"
              className={showKasirPointer ? 'kasir-guide-target-button' : undefined}
              onClick={handleKasirClick}
              icon={
                <img
                  className="shortcut-icon"
                  src={`${SHORTCUT_ICON_BASE}/action-kasir.svg`}
                  alt=""
                />
              }
            />
          )}
          <ShortcutBtn
            label={searchLabel}
            showTooltip={isIconOnlyShortcut}
            icon={
              <img className="shortcut-icon" src={`${SHORTCUT_ICON_BASE}/action-cari.svg`} alt="" />
            }
            onClick={onOpenGlobalSearch}
          />
        </Space>
      )}
      <Space size={12} align="center" className="topbar-end">
        <div>
          <CompanySelector
            selectedCompany={selectedCompany}
            companies={companies}
            onChangeCompany={onChangeCompany}
            isCanAddCompany={isSuperUser}
          />
        </div>
        <div>
          <LanguageSelector />
        </div>
        {isHasAccessAudit && (
          <div>
            <Timelines />
          </div>
        )}
        <div>
          <Actions />
        </div>
        <div>
          <ThemeColors />
        </div>
        <ProfileMenu />
      </Space>

      {showKasirPointer && (
        <div className="kasir-guide-overlay" key={arrowNonce} aria-hidden="true">
          <div className="kasir-guide-backdrop" />

          <svg
            className="kasir-guide-svg"
            viewBox={`0 0 ${viewportSize.width} ${viewportSize.height}`}
          >
            {arrowTransform ? (
              <g transform={arrowTransform} className="kasir-doodle-arrow-group">
                <g
                  ref={kasirArrowBobRef}
                  className="kasir-guide-arrow-bob"
                  style={{ transformBox: 'fill-box' }}
                >
                  <image
                    className="kasir-guide-arrow-image kasir-guide-arrow-image--white"
                    href={arrowSvgUrl}
                    x={0}
                    y={0}
                    width={KASIR_ARROW_ASSET_SIZE}
                    height={KASIR_ARROW_ASSET_SIZE}
                    preserveAspectRatio="xMidYMid meet"
                  />
                </g>
              </g>
            ) : null}
            {arrowTransform && ctaLabelPos ? (
              <g transform={`translate(${ctaLabelPos.x}, ${ctaLabelPos.y})`}>
                {/*
                  Bob animation memakai style.transform dari Motion — harus di <g> terpisah
                  agar tidak menimpa translate(x,y) posisi label (tanpa ini teks ke pojok kiri atas).
                */}
                <g ref={kasirCtaLabelBobRef} className="kasir-guide-cta-label-bob">
                  <text
                    className="kasir-guide-cta-text"
                    x={0}
                    y={0}
                    dominantBaseline="middle"
                    textAnchor="start"
                  >
                    Coba Sekarang
                  </text>
                </g>
              </g>
            ) : null}
          </svg>
        </div>
      )}
    </div>
  )
}

export default memo(CustomTopBar, compareWithJSON)
